/**
 * Enrichment presentation ↔ canonical wire mapping for the Import Detail
 * contacts table (subtask 04). Single place the FE translates the
 * `email_source` / `work_email_source` provenance and the `enrichment_error`
 * code into human copy + design-system pill colours (no inline hex — mirrors
 * `tagDefs.ts`).
 */
import type { EmailSource } from "@nu-assist/contracts";

/** A source badge's label + the design-system pill colour classes. */
export interface SourceBadgeDef {
  label: string;
  /** Tailwind classes (design-system tokens) for the pill surface. */
  pillClass: string;
}

/**
 * Only the two ENRICHMENT sources get a badge — `csv` / `manual` /
 * `thrive_match` / `rocketreach` are "where the file/Thrive already had it",
 * not an enrichment result worth calling out, so they map to `undefined`
 * (no badge). `nu_sync` = our own free data (success-adjacent, like the
 * `enriched` tag); `hellosky` = the external paid source (info token, like
 * `combined`).
 */
export const SOURCE_BADGE_BY_VALUE: Partial<Record<EmailSource, SourceBadgeDef>> = {
  nu_sync: {
    label: "via nu_sync",
    pillClass: "bg-success-muted text-success-ink border border-success-strong",
  },
  hellosky: {
    label: "via HelloSky",
    pillClass: "bg-info-muted text-info border border-info-strong",
  },
};

/**
 * Human copy for each `enrichment_error` code (only set when
 * `enrichment_status === "failed"`). An unknown/future code falls back to a
 * generic sentence rather than rendering the raw token.
 */
export const ENRICHMENT_ERROR_COPY: Record<string, string> = {
  not_found: "No email found",
  no_credits: "HelloSky credits exhausted",
  api_error: "Lookup failed",
};

/** Map an `enrichment_error` code to its human tooltip copy. */
export function enrichmentErrorCopy(error: string | null | undefined): string {
  if (!error) return "Enrichment failed";
  return ENRICHMENT_ERROR_COPY[error] ?? "Enrichment failed";
}
